import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Typography, ConfigProvider, Alert, Select } from 'antd';
import { PhoneOutlined, ArrowRightOutlined, GoogleOutlined, AppleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import authActions from '../../actions/authActions';
import { preferenceActions } from '../../actions/preferenceActions';
import AnimatedDrivingScene from '../../components/svg/animatedDrivingScene';
import { getblobLink } from '../../services/helper';

const { Title, Text } = Typography;

const Login: React.FC = () => {
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [phoneNumber, setphoneNumber] = useState<any>({ mobileNumber: '', isMobile: false, });
  const authlogin = useSelector((state: any) => state.authReducer);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; text?: string }>({ type: null });
  const authErrorMessage = authlogin?.isError
    ? (authlogin?.error?.message || authlogin?.error?.errorMessage || authlogin?.error?.response?.data?.errorMessage || 'Unable to sign in right now.')
    : null;

  const redirectTo = searchParams.get('redirect');
  const plan = searchParams.get('plan');
  const billing = searchParams.get('billing');

  useEffect(() => {
    sessionStorage.removeItem('OTP_ACTIVE_MOBILE');
  }, []);

  const onFinish = () => {
    dispatch(authActions.clearDeviceLimit());
    dispatch(authActions.otpLoginRequest(phoneNumber));
    if (phoneNumber.mobileNumber) {
      dispatch(preferenceActions.getPreferenceByPhone(phoneNumber.mobileNumber));
    }
  };

  useEffect(() => {
    if (authlogin && authlogin?.isSuccess) {
      sessionStorage.setItem('OTP_ACTIVE_MOBILE', phoneNumber.mobileNumber);
      const state: any = { mobileNumber: phoneNumber.mobileNumber };
      if (redirectTo && plan && billing) {
        state.redirectTo = redirectTo;
        state.plan = plan;
        state.billing = billing;
      }

      navigate('/verify-otp', { state });
      authlogin.isSuccess = false;
    }
  }, [authlogin, navigate, phoneNumber.mobileNumber, redirectTo, plan, billing]);


  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#f97316', borderRadius: 8, colorTextBase: '#1f2937', }, }}>
      <div className="split-layout-wrapper">
        <div className="cursor-pointer h-fit ml-[70px] mt-[40px]" onClick={() => { navigate("/") }} >
          <img src={`${getblobLink()}/public/asset/logo_with_name.png`} alt="DriveMynt Logo"
          loading="lazy" width="180" height="180"/>
        </div>
        <div className="split-left">
          <AnimatedDrivingScene
            title="Master the Road"
            subtitle="Your journey to becoming a confident driver starts here. Professional instruction, modern tools."
            hasError={authlogin?.isError}
          />
        </div>
        <div className="split-right" >
          <div className="auth-bg-decoration">
            <div className="floating-shape shape-1"></div>
            <div className="floating-shape shape-2"></div>
            <div className="floating-shape shape-3"></div>
            <div className="floating-shape shape-4"></div>
          </div>
          <div className="auth-form-container">
            <div className='auth-form-background'>
              <div className="auth-form-content">
                <Title level={2} className="auth-title">Welcome back</Title>
                <Text className="auth-subtitle left-align">
                  Log in to manage your driving lessons and progress
                </Text>
                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  message="Currently, the OTP will be sent to the email registered with this mobile number."
                  style={{ marginBottom: 12 }}
                />

                {/* Show selected plan info if coming from pricing */}
                {plan && billing && (
                  <div style={{ marginBottom: 20, marginTop: 16 }}>
                    <Alert type="info" message={`Selected Plan: ${plan}`} description={`Billing: ${billing === 'yearly' ? 'Yearly (Save 20%)' : 'Monthly'}. Complete login to continue with your subscription.`} showIcon />
                  </div>
                )}

              
                <Form name="login_phone_form" layout="vertical"
                  className="auth-form" requiredMark={false} onFinish={onFinish}>
                  <Form.Item name="phone" label="Phone Number"
                    rules={[{ required: true, message: 'Please enter your phone number' }, { pattern: /^\d{10}$/, message: 'Enter a valid 10-digit mobile number' },]}>
                    <Input placeholder="Enter your mobile number" size="large"
                      onKeyDown={(e) => {
                        const allowedKeys = ['Enter', "Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab"];
                        if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
                        if (!/^\d$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => setphoneNumber({ ...phoneNumber, mobileNumber: e.target.value.replace(/\D/g, ''), })}
                      addonBefore={
                        <PhoneOutlined style={{ color: '#5B8DEF' }} />
                      } maxLength={10} className="auth-phone-input"
                    />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 20 }}>
                    <Button type="primary" htmlType="submit"
                      size="large" block loading={authlogin?.isLoading}
                      className="auth-submit-btn" icon={<ArrowRightOutlined />}
                      iconPosition="end">
                      Get OTP
                    </Button>
                  </Form.Item>
                  <div className="auth-legal" style={{ marginTop: 16, textAlign: 'center' }}>
                    <Text className="auth-legal-text">
                      Don't have an account?&nbsp;
                      <a href={plan ? `/register?plan=${encodeURIComponent(plan)}&billing=${billing}` : '/register'}
                        className="link-primary"
                        style={{ fontWeight: 600 }}
                      >
                        Register here
                      </a>
                    </Text>
                  </div>
                </Form>
              </div>
            </div>
          </div>

        </div>
      </div>
    </ConfigProvider>
  );
};

export default Login;
